import { GeneralResMock } from '../types/general.type'
import { ProductInventoryResMock } from '../types/inventory.type'
import { ProductData } from '../types/product.type'
import { concatenateStrengthsConcise } from '../utils/helper'
import AXIOS_INSTANCE from './axiosInstance'

export type RequisitionProductOption = {
  value: string
  label: string
  brand_name: string
  dosage_form: string
  generic_name: string
  strength: string
}

export type RequisitionFieldOption = {
  label: string
  value: string
}

export const requisitionProductAction = async (
  query?: string,
  fallbackQuery?: string
): Promise<RequisitionProductOption[]> => {
  try {
    const q = query?.trim() || fallbackQuery?.trim() || ''

    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<ProductData[]>>('/products', {
      params: {
        ...(query && { query }),
        ...(fallbackQuery && { brand_name: fallbackQuery }),
        'page[size]': 100,
        sort_by: 'created_at',
        sort_dir: 'desc'
      }
    })

    return (data?.data || []).map(product => {
      const brand_name = product.attributes?.brand_name || ''
      const generic_name = product.attributes?.generic_name || ''
      const dosage_form = product.attributes?.dosage_form || ''
      const strength = product.attributes?.strength || concatenateStrengthsConcise(product.attributes?.api_strengths)
      const dosagePrefix = dosage_form ? `${dosage_form} - ` : ''
      const strengthSuffix = strength && strength !== '-' ? ` (${strength})` : ''
      // Prefer generic with brand in parentheses so same-generic products stay distinguishable.
      const namePart = generic_name && brand_name ? `${generic_name} (${brand_name})` : generic_name || brand_name

      return {
        brand_name,
        dosage_form,
        generic_name,
        label: `${dosagePrefix}${namePart}${strengthSuffix}`,
        strength: strength === '-' ? '' : strength,
        value: product.id
      }
    })
  } catch {
    return []
  }
}

export const requisitionBrandNameAction = async (
  productId?: string | number,
  query?: string
): Promise<RequisitionFieldOption[]> => {
  try {
    if (!productId) return []

    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<ProductData>>(`/products/${productId}`)
    const brandName = data?.data?.attributes?.brand_name || ''
    if (!brandName) return []

    if (query?.trim() && !brandName.toLowerCase().includes(query.trim().toLowerCase())) {
      return []
    }

    return [{ label: brandName, value: brandName }]
  } catch (error: any) {
    return error?.response || []
  }
}

export const findProductByBrandName = async (brandName?: string): Promise<RequisitionProductOption | null> => {
  try {
    if (!brandName?.trim()) return null

    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<ProductData[]>>('/products', {
      params: {
        query: brandName.trim(),
        'page[size]': 100
      }
    })

    const matchedProduct = (data?.data || []).find(
      product => product.attributes?.brand_name?.toLowerCase() === brandName.toLowerCase()
    )

    if (!matchedProduct) return null

    const generic_name = matchedProduct.attributes?.generic_name || ''
    const dosage_form = matchedProduct.attributes?.dosage_form || ''
    const strength =
      matchedProduct.attributes?.strength || concatenateStrengthsConcise(matchedProduct.attributes?.api_strengths)
    const dosagePrefix = dosage_form ? `${dosage_form} - ` : ''

    return {
      brand_name: brandName,
      dosage_form,
      generic_name,
      label: `${dosagePrefix}${generic_name}${strength && strength !== '-' ? ` (${strength})` : ''}`,
      strength: strength === '-' ? '' : strength,
      value: matchedProduct.id
    }
  } catch (error: any) {
    return null
  }
}

export const requisitionApiStrengthAction = async (
  productId?: string | number,
  query?: string
): Promise<RequisitionFieldOption[]> => {
  try {
    if (!productId) return []

    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<ProductData>>(`/products/${productId}`)
    const strengths = data?.data?.attributes?.strength
      ? [data.data.attributes.strength]
      : ((data?.data?.attributes?.api_strengths || []).map(({ strength }) => strength).filter(Boolean) as string[])

    const filtered = query?.trim()
      ? strengths.filter(item => item.toLowerCase().includes(query.trim().toLowerCase()))
      : strengths

    return filtered.map(item => ({ label: item, value: item }))
  } catch (error: any) {
    return error?.response || []
  }
}

export const genericNameAction = async (query?: string): Promise<{ label: string; value: string }[]> => {
  try {
    const genericNameList: { label: string; value: string }[] = []

    let endpoint = 'products?'
    if (query) endpoint += `query=${encodeURIComponent(query)}&`

    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<ProductData[]>>(endpoint)
    const uniqueGenericNames = new Set<string>()

    ;(data.data || []).forEach(product => {
      const genericName = product.attributes?.generic_name
      if (genericName && !uniqueGenericNames.has(genericName)) {
        uniqueGenericNames.add(genericName)
        genericNameList.push({
          label: genericName,
          value: genericName
        })
      }
    })

    return genericNameList
  } catch (error: any) {
    return error?.response || []
  }
}

export const brandNameAction = async (
  query?: string,
  defaultsearchQuery?: string
): Promise<{ label: string; value: string }[]> => {
  try {
    const brandNameList: { label: string; value: string }[] = []

    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<ProductData[]>>('/products', {
      params: {
        ...(query && { query }),
        ...(defaultsearchQuery && { generic_name: defaultsearchQuery }),
        'page[size]': 100
      },
      skipNavigationFeedback: true
    })
    const uniqueBrandNames = new Set<string>()

    ;(data.data || []).forEach(product => {
      const brandName = product.attributes?.brand_name
      if (brandName && !uniqueBrandNames.has(brandName)) {
        uniqueBrandNames.add(brandName)
        brandNameList.push({
          label: brandName,
          value: brandName
        })
      }
    })

    return brandNameList
  } catch {
    return []
  }
}
export const inventoryAttributeAction = async (
  productId: string,
  atr: 'batch_number' | 'stock_location' | 'date_of_manufacture',
  query?: string
): Promise<{ label: string; value: string }[]> => {
  try {
    const attributeList: { label: string; value: string }[] = []

    let endpoint = 'product_inventories?'
    if (query) endpoint += `query=${encodeURIComponent(query)}&`
    if (productId) endpoint += `product_id=${productId}&`

    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<ProductInventoryResMock[]>>(endpoint, {
      skipNavigationFeedback: true
    })
    const uniqueAttribute = new Set<string>()

    ;(data.data || []).forEach(product => {
      const attribute = product?.attributes?.[`${atr!}`]
      if (attribute && !uniqueAttribute.has(attribute)) {
        uniqueAttribute.add(attribute)
        attributeList.push({
          label: attribute,
          value: attribute
        })
      }
    })

    return attributeList
  } catch (error: any) {
    return error?.response || []
  }
}

export type DynamicProductInventoryResult = {
  data: ProductInventoryResMock[]
  included: unknown[]
  meta?: unknown
}

export const dynamicProductInventoryAction = async (
  arg: Record<string, string>
): Promise<DynamicProductInventoryResult> => {
  try {
    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<ProductInventoryResMock[], unknown[]>>(
      '/product_inventories',
      {
        params: { ...arg, 'page[size]': '100' }
      }
    )
    return {
      data: data?.data ?? [],
      included: data?.included ?? [],
      meta: data?.meta
    }
  } catch {
    return { data: [], included: [] }
  }
}
